import { FilterTypes } from 'hb-react/frontoffice/apps/Filters/constants/FilterTypes';
import {
  Checkbox,
  DateRange,
  DoubleMultiSelect,
  Location,
  Number,
  NumberRange,
  SelectArray,
  SelectBoolean,
  SelectSearchAndOptions,
  Text,
} from 'hb-react/shared/components/formik/components/inputs';

const Filter = ({ type, ...otherProps }) => {
  const otherPropsWithFormikFlag = {
    ...otherProps,
  };

  switch (type) {
    case FilterTypes.KEYWORD:
    case FilterTypes.FREE_TEXT:
      return <KeywordFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.NUMBER_RANGE:
    case FilterTypes.DECIMAL_RANGE:
      return <NumberRangeFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.YEAR_RANGE:
      return <YearRangeFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.DATE_RANGE:
      return <DateRangeFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.LOCATION:
      return <LocationFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.CHECKBOX:
      return <CheckboxFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.BOOLEAN:
      return <SelectBooleanFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.SELECT:
      return <MultiSelectFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.FULLTEXT_SELECT:
      return <FullTextSelectFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.DOUBLE_SELECT:
      return <DoubleMultiSelectFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.INDUSTRIES:
      return <IndustriesFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.SKILLS:
      return <SkillsFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.NATIONALITIES:
      return <NationalitiesFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.SUB_NETWORKS:
      return <SubnetworksFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.COMPANIES:
      return <CompaniesFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.SCHOOLS:
      return <SchoolsFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.FIELD_OF_STUDIES:
      return <FieldOfStudiesFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.DEGREES:
      return <DegreesFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.EDUCATIONS_YEAR_FROM:
    case FilterTypes.EDUCATIONS_YEAR_TO:
      return <NumberFilter {...otherPropsWithFormikFlag} />;

    case FilterTypes.SELECT_WITH_SEARCH_AND_OTHER_OPTIONS:
      return <SelectSearchAndOptionsFilter {...otherPropsWithFormikFlag} />;

    default:
      return null;
  }
};

const KeywordFilter = ({ item, ...otherProps }) => (
  <Text
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    {...otherProps}
  />
);

const NumberRangeFilter = ({ item, ...otherProps }) => (
  <NumberRange
    maxName={`query[max_${item.name}]`}
    minName={`query[min_${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={{
      min: s__('Filters|Type minimum value'),
      max: s__('Filters|Type maximum value'),
    }}
    {...otherProps}
  />
);

const YearRangeFilter = ({ item, ...otherProps }) => (
  <NumberRange
    maxName={`query[max_${item.name}]`}
    minName={`query[min_${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={{
      min: s__('Filters|Type minimum year'),
      max: s__('Filters|Type maximum year'),
    }}
    validate={{ minimum: 1920, maximum: 2119 }}
    {...otherProps}
  />
);

const DateRangeFilter = ({ item, ...otherProps }) => (
  <DateRange
    maxName={`query[max_${item.name}]`}
    minName={`query[min_${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={{
      min: s__('Filters|Select minimum date'),
      max: s__('Filters|Select maximum date'),
    }}
    {...otherProps}
  />
);

const LocationFilter = ({
  handleLocationFilterChange,
  handleUpdateOnline,
  item,
  inputRef,
  onChange,
  ...otherProps
}) => {
  const handleMapPositionUpdate = (e) => {
    if (onChange) {
      onChange(e);
    }

    if (handleLocationFilterChange) {
      handleLocationFilterChange(e);
    }
  };

  return (
    <Location
      // Will be better if we can handle a name for current_location rather than everything flat under query, will see with back-end
      // name={`query[${item.name}]`}
      name="query"
      label={item.label}
      labelMultiline
      placeholder={item.placeholder}
      componentRef={inputRef}
      onlineEnabled={item.onlineEnabled}
      onlineName={item.onlineName}
      onlineLabel={item.onlineLabel}
      onUpdateOnline={handleUpdateOnline}
      onChange={handleMapPositionUpdate}
      {...otherProps}
    />
  );
};

const CheckboxFilter = ({ item, ...otherProps }) => (
  <Checkbox
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    labelHtml={{ className: 'weight--normal' }}
    {...otherProps}
  />
);

const SelectBooleanFilter = ({ item, ...otherProps }) => (
  <SelectBoolean
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={s__('Filters|Select yes or no')}
    isClearable
    isNotSearchable
    {...otherProps}
  />
);

const MultiSelectFilter = ({ item, ...otherProps }) => (
  <SelectArray
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={s__('Filters|Select one or more options')}
    options={item.options}
    multiple
    isClearable
    isNotSearchable={item.options?.length <= 10}
    {...otherProps}
  />
);

const FullTextSelectFilter = ({ item, ...otherProps }) => {
  const { inputHtml, ...props } = otherProps;

  return (
    <SelectArray
      name={`query[${item.name}]`}
      label={item.label}
      labelMultiline
      placeholder={s__('Filters|Type a keyword or select one or more options')}
      options={item.options}
      inputHtml={{
        formatCreateLabel: (s) => `${__('Search for')}: ${s}`,
        ...inputHtml,
        creatable: true,
      }}
      multiple
      creatable
      {...props}
    />
  );
};

const SelectSearchAndOptionsFilter = ({ item, ...otherProps }) => (
  <SelectSearchAndOptions
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder || s__('Filters|Select one or more options')}
    showValueAsPlaceholder={item.showValueAsPlaceholder}
    options={item.options}
    shouldResetFormikSearchValueOnOtherOptionSelection={
      item.shouldResetFormikSearchValueOnOtherOptionSelection
    }
    noOptionsMessage={item.noOptionsMessage}
    isClearable
    {...otherProps}
  />
);

const DoubleMultiSelectFilter = ({ item, ...otherProps }) => (
  <DoubleMultiSelect
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={s__('Filters|Select an option')}
    options={item.json_options?.options}
    isClearable
    {...otherProps}
  />
);

const IndustriesFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="industries"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const SkillsFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="skills"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const NationalitiesFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="nationalities"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const SubnetworksFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="sub_networks"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const CompaniesFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="companies"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const SchoolsFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="schools"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const FieldOfStudiesFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="field_of_studies"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const DegreesFilter = ({ item, ...otherProps }) => (
  <SelectArray
    type="degrees"
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    multiple
    isClearable
    {...otherProps}
  />
);

const NumberFilter = ({ item, ...otherProps }) => (
  <Number
    name={`query[${item.name}]`}
    label={item.label}
    labelMultiline
    placeholder={item.placeholder}
    {...otherProps}
  />
);

const Item = ({ filter, ...otherProps }) => (
  <Filter
    item={filter}
    type={filter.type}
    {...otherProps}
    menuShouldBlockScroll
  />
);

export default Item;
